// TEN15 (docs/build-briefs/TEN15-franchisor-console-shell-and-franchise-list.md; DEC-126, DEC-338, DEC-339,
// DEC-342): the pure decisions behind the franchisor console's franchise list. No DOM, no fetch:
// test/ten15-franchisor-console-model.test.ts proves it. The page (franchisorMain.ts) is wiring only.
//
// What the list shows is exactly what GET /franchisor/franchisees sends: a name, a registry status
// (TEN15-F1, DEC-344: no joined date). There is no location count (it would read a tenant-classed table, TEN8-F5), no royalty, no invoice and no
// operational figure, and a row is not a link (a franchisee detail view is blocked by DEC-126).

import { sortByName } from "./nameSort.ts";

export const FRANCHISEE_STATUS_VALUES = ["provisioned", "active", "suspended", "offboarded"] as const;
export type FranchiseeStatusValue = (typeof FRANCHISEE_STATUS_VALUES)[number];

/** One row as the server sends it. */
export interface FranchiseeDto {
  /** TEN20: the opaque reference the figures endpoint also carries. */
  franchiseeRef: string;
  name: string;
  status: FranchiseeStatusValue;
}

export interface FranchiseesResponse {
  network: { name: string };
  franchisees: FranchiseeDto[];
}

export function isFranchiseesShape(value: unknown): value is FranchiseesResponse {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  const network = v["network"] as Record<string, unknown> | undefined;
  if (typeof network !== "object" || network === null || typeof network["name"] !== "string") return false;
  const rows = v["franchisees"];
  if (!Array.isArray(rows)) return false;
  return rows.every((row) => {
    if (typeof row !== "object" || row === null) return false;
    const r = row as Record<string, unknown>;
    return (
      typeof r["franchiseeRef"] === "string" &&
      typeof r["name"] === "string" &&
      (FRANCHISEE_STATUS_VALUES as readonly string[]).includes(r["status"] as string)
    );
  });
}

export interface StatusPresentation {
  label: string;
  /** Which existing pill colour: neutral, good, warn or bad. No new colours. */
  tone: "neutral" | "good" | "warn" | "bad";
}

/** The same words and colours the software owner console uses for the same registry status
 * (test/ten15-franchisor-console-model.test.ts holds the two together). */
export function franchiseeStatusPresentation(status: FranchiseeStatusValue): StatusPresentation {
  switch (status) {
    case "provisioned":
      return { label: "Set up, not yet signed in", tone: "neutral" };
    case "active":
      return { label: "Active", tone: "good" };
    case "suspended":
      return { label: "Suspended", tone: "warn" };
    case "offboarded":
      return { label: "Offboarded", tone: "bad" };
  }
}

export interface FranchiseeRowView {
  franchiseeRef: string;
  name: string;
  statusLabel: string;
  tone: StatusPresentation["tone"];
}

/** The rows A to Z by name (DEC-339), the order the page shows regardless of the server's order. */
export function franchiseeRowViews(rows: readonly FranchiseeDto[]): FranchiseeRowView[] {
  return sortByName(rows, (row) => row.name).map((row) => {
    const status = franchiseeStatusPresentation(row.status);
    return { franchiseeRef: row.franchiseeRef, name: row.name, statusLabel: status.label, tone: status.tone };
  });
}

export const FRANCHISOR_NAV = [
  { id: "franchisees", label: "Franchisees" },
] as const;

export const FRANCHISEES_TITLE = "Franchisees";
export const FRANCHISEES_LOADING = "Loading franchisees...";
export const FRANCHISEES_EMPTY_TITLE = "No franchisees yet.";
export const FRANCHISEES_FAILED = "The franchisee list could not be loaded. Try again.";

export type ListState = "empty" | "rows";
export function listStateFor(rows: readonly unknown[]): ListState {
  return rows.length === 0 ? "empty" : "rows";
}

// ---- TEN20: net sales, royalty due, royalty collected and fund fee due per franchisee (DEC-344, DEC-419, DEC-490) -------------------------------

export const FIGURES_PERMISSION = "network.reports.run";

/** Whether the figures are offered: a permission read, never a role name. The Viewer keeps the plain list. */
export function canSeeFigures(permissions: readonly string[]): boolean {
  return permissions.includes(FIGURES_PERMISSION);
}

/** One franchisee's figures as GET /franchisor/franchisees/figures sends them; null is "nothing behind it". */
export interface FigureDto {
  franchiseeRef: string;
  netSalesMtdCents: number | null;
  netSalesYtdCents: number | null;
  royaltyDueMtdCents: number | null;
  royaltyDueYtdCents: number | null;
  royaltyCollectedMtdCents: number | null;
  royaltyCollectedYtdCents: number | null;
  /** FRADFUND1: the brand or ad fund fee due, shown beside royalty and never inside it. Absent from an older server. */
  fundDueMtdCents?: number | null;
  fundDueYtdCents?: number | null;
}

export interface FiguresResponse {
  franchisees: FigureDto[];
}

const FIGURE_KEYS = [
  "netSalesMtdCents",
  "netSalesYtdCents",
  "royaltyDueMtdCents",
  "royaltyDueYtdCents",
  "royaltyCollectedMtdCents",
  "royaltyCollectedYtdCents",
] as const;

/** FRADFUND1: the two fund fee cells, which an older server does not send. */
const FUND_FIGURE_KEYS = ["fundDueMtdCents", "fundDueYtdCents"] as const;

export function isFiguresShape(value: unknown): value is FiguresResponse {
  if (typeof value !== "object" || value === null) return false;
  const rows = (value as Record<string, unknown>)["franchisees"];
  if (!Array.isArray(rows)) return false;
  return rows.every((row) => {
    if (typeof row !== "object" || row === null) return false;
    const r = row as Record<string, unknown>;
    return (
      typeof r["franchiseeRef"] === "string" &&
      FIGURE_KEYS.every((key) => r[key] === null || (typeof r[key] === "number" && Number.isSafeInteger(r[key]))) &&
      FUND_FIGURE_KEYS.every(
        (key) => r[key] === undefined || r[key] === null || (typeof r[key] === "number" && Number.isSafeInteger(r[key])),
      )
    );
  });
}

/** The two header rows: a group per measure, each with Month to date and Year to date. */
export const FIGURE_GROUPS = ["Net sales", "Royalty due", "Royalty collected", "Fund fee due"] as const;
export const FIGURE_PERIODS = ["MTD", "YTD"] as const;
export const FIGURE_EMPTY = "-";

/** 123456 -> "$1,234.56"; -250 -> "-$2.50"; null -> "-". */
export function figureMoney(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return FIGURE_EMPTY;
  const magnitude = Math.abs(cents);
  const dollars = Math.trunc(magnitude / 100).toLocaleString("en-US");
  return `${cents < 0 ? "-" : ""}$${dollars}.${String(magnitude % 100).padStart(2, "0")}`;
}

/** The eight cells of a row in column order; every cell is N/A when the franchisee has no figures row. */
export function figureCells(figure: FigureDto | undefined): string[] {
  return [...FIGURE_KEYS, ...FUND_FIGURE_KEYS].map((key) => figureMoney(figure === undefined ? null : figure[key]));
}
